import { Injectable, Injector, TemplateRef } from '@angular/core';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { Overlay } from '@angular/cdk/overlay';
import { PhxToastContainerComponent } from './phx-toast-container.component';
import { PhxToastContent, PhxToastData, PhxToastOptions, PhxToastRef, PhxToastType } from './phx-toast.types';

let globalCounter = 0;

@Injectable({
  providedIn: 'root',
})
export class PhxToastService {
  private _componentPrefix: string = 'phx-toast';
  private _container?: PhxToastContainerComponent;
  private _config?: PhxToastOptions;

  constructor(private _overlay: Overlay, private _injector: Injector) {}

  public remove(id: string): void {
    if (this._container) {
      this._container.remove(id);
    }
  }

  public removeAll(): void {
    if (this._container) {
      this._container.removeAll();
    }
  }

  public info(content: PhxToastContent, options?: PhxToastOptions): PhxToastRef {
    return this.createInstance('info', content, options);
  }

  public positive(content: PhxToastContent, options?: PhxToastOptions): PhxToastRef {
    return this.createInstance('positive', content, options);
  }

  public negative(content: PhxToastContent, options?: PhxToastOptions): PhxToastRef {
    return this.createInstance('negative', content, options);
  }

  public caution(content: PhxToastContent, options?: PhxToastOptions): PhxToastRef {
    return this.createInstance('caution', content, options);
  }

  public show(type: PhxToastType, content: PhxToastContent, options?: PhxToastOptions): PhxToastRef {
    return this.createInstance(type, content, options);
  }

  public configure(config: PhxToastOptions): void {
    this._config = config;
  }

  private createInstance(type: PhxToastType, content: PhxToastContent, options?: PhxToastOptions): PhxToastRef {
    if (!this._container) {
      this._container = this.getContainerInstance(PhxToastContainerComponent);
    }

    const _content: PhxToastData = typeof content === 'string' || content instanceof TemplateRef ? { content } : content;

    const toastInstance = this._container.create({
      type,
      toastData: _content,
      createdAt: new Date(),
      toastId: this.getInstanceId(),
      options: this.mergeOptions(options),
    });

    return {
      toastId: toastInstance.toastId,
      onClose: toastInstance.onClose,
    };
  }

  private getInstanceId(): string {
    return `${this._componentPrefix}-${globalCounter++}`;
  }

  private mergeOptions(options?: PhxToastOptions): PhxToastOptions {
    return { ...(this._config || {}), ...(options || {}) };
  }

  private getContainerInstance(ctor: ComponentType<PhxToastContainerComponent>): PhxToastContainerComponent {
    const overlayRef = this._overlay.create({
      hasBackdrop: false,
      scrollStrategy: this._overlay.scrollStrategies.noop(),
      positionStrategy: this._overlay.position().global().centerHorizontally().top(`${this._config?.phxOffsetTop || 0}px`),
    });

    const componentPortal = new ComponentPortal(ctor, null, this._injector);
    const componentRef = overlayRef.attach(componentPortal);
    const overlayWrapper = overlayRef.hostElement;
    overlayWrapper.style.zIndex = '1010';
    overlayWrapper.style.height = 'min-content';

    const containerInstance = componentRef.instance;
    containerInstance.afterAllToastsRemoved.subscribe(() => {
      this._container = undefined;
      overlayRef.dispose();
    });

    return containerInstance;
  }
}
